// [목적] 운영 콘솔 셸 — /auth/me 로그인·권한 확인 게이트 + 상태 기반 4탭(통계·수집 이력·프롬프트·신고)
// [이유] M9 AdminPage. react-router 미설치 + 의존성 추가 금지 → useState 탭 전환.
//         로그인은 apps/web OAuth 흐름 재사용(쿠키 공유) — admin 은 로그인 UI 를 갖지 않음.
// [사이드 이팩트] 마운트 시 GET /auth/me (401 이면 api() 가 /auth/refresh 1회 시도).
// [수정 시 고려사항] 탭 권한은 BE @Roles 와 동기(수집 이력·프롬프트 = admin 전용). FE 숨김은 UX 용 — 실제 차단은 BE.
//                   VITE_WEB_ORIGIN 미설정 시 로컬 web(localhost:5173) 로그인으로 안내.

import { AppBar, Box, Button, Container, Tab, Tabs, Toolbar, Typography } from '@mui/material';
import { useState } from 'react';

import { api, useAsync, type Role } from './api';
import { IngestPanel, PromptsPanel, ReportsPanel, StatsPanel } from './panels';

const WEB_ORIGIN =
  (import.meta.env['VITE_WEB_ORIGIN'] as string | undefined) ?? 'http://localhost:5173';

const TABS = [
  { id: 'stats', label: '통계', roles: ['admin', 'editor'], Panel: StatsPanel },
  { id: 'ingest', label: '수집 이력', roles: ['admin'], Panel: IngestPanel },
  { id: 'prompts', label: '프롬프트', roles: ['admin'], Panel: PromptsPanel },
  { id: 'reports', label: '신고 처리', roles: ['admin', 'editor'], Panel: ReportsPanel },
] as const;

function Notice(props: { title: string; body: string; action?: { label: string; href: string } }) {
  return (
    <Container maxWidth="sm" sx={{ py: 16, textAlign: 'center' }}>
      <Typography variant="h3" component="h1" gutterBottom>
        {props.title}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 6 }}>
        {props.body}
      </Typography>
      {props.action && (
        <Button variant="contained" href={props.action.href}>
          {props.action.label}
        </Button>
      )}
    </Container>
  );
}

export function App() {
  const me = useAsync(() => api<{ handle: string; role: Role }>('/auth/me'));
  const [tab, setTab] = useState(0);

  if (me.loading) {
    return <Notice title="확인 중…" body="로그인 상태를 확인하고 있습니다." />;
  }
  if (me.error?.status === 401) {
    return (
      <Notice
        title="로그인이 필요합니다"
        body="강원캔버스 웹에서 운영자 계정으로 로그인한 뒤 이 페이지를 새로고침하세요."
        action={{ label: '웹에서 로그인', href: `${WEB_ORIGIN}/login` }}
      />
    );
  }
  if (me.error || !me.data) {
    return <Notice title="오류" body={me.error?.message ?? '사용자 정보를 불러오지 못했습니다.'} />;
  }
  const role = me.data.role;
  const tabs = TABS.filter((t) => (t.roles as readonly Role[]).includes(role));
  if (tabs.length === 0) {
    return (
      <Notice
        title="접근 권한이 없습니다"
        body={`@${me.data.handle} 계정은 운영자(admin/editor) 권한이 없습니다. 관리자에게 권한을 요청하세요.`}
      />
    );
  }
  const current = tabs[Math.min(tab, tabs.length - 1)]!;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar sx={{ gap: 4 }}>
          <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
            강원캔버스 운영 콘솔
          </Typography>
          <Typography variant="body2" color="text.secondary">
            @{me.data.handle} · {role}
          </Typography>
        </Toolbar>
        <Tabs
          value={Math.min(tab, tabs.length - 1)}
          onChange={(_, v: number) => setTab(v)}
          aria-label="운영 메뉴"
          variant="scrollable"
        >
          {tabs.map((t) => (
            <Tab key={t.id} label={t.label} id={`tab-${t.id}`} aria-controls={`panel-${t.id}`} />
          ))}
        </Tabs>
      </AppBar>
      <Container
        maxWidth="lg"
        role="tabpanel"
        id={`panel-${current.id}`}
        aria-labelledby={`tab-${current.id}`}
        sx={{ py: 6 }}
      >
        <current.Panel key={current.id} />
      </Container>
    </Box>
  );
}
